import { v4 as uuid } from 'uuid';
import { IFile, OnboardingStatus, WorkerInvitationStatus } from 'models/general';
import { StepKey } from 'modules/views/shared/Wizard/config';

//! Remove unnecessary state properties after the project is complete
interface IOnboardingState {
  id?: string | null;
  userId?: string | null;
  firstName?: string;
  middleName?: string;
  lastName?: string;
  email?: string | null;
  mobilePhoneNumber?: string | null;
  phoneNumber?: string | null; // Alternate phone number (top-level field)
  dateOfBirth?: string;
  gender?: number;
  ethnicity?: { id: string; name: string };
  primaryLanguage?: { id: string; name: string };
  isVeteran?: boolean;
  allergies?: string;
  socialSecurityNumber?: string;
  identificationType?: { id: string; name: string };
  identificationNumber?: string;
  identificationGeographicLocation?: { id: string; name: string };
  otherIdentificationType?: string;
  signedBiometricConsentForm?: boolean;
  signatureUrl?: string;
  acceptedStandardConsentIds?: string[];
  acceptedCustomConsentIds?: string[];
  certifications?: any[];
  loginWith?: string | null;
  inviteMethod?: number;
  address?: any;
  emergencyContactName?: string | null;
  emergencyContactPhone?: string | null;
  emergencyContactRelationship?: string | null;
  canEditEmail?: boolean;
  canEditMobilePhoneNumber?: boolean;
  existingConsentFormForAllCompanies?: any;
  status?: string | number | null;
  invitationStatus?: WorkerInvitationStatus;
  /**
   * True when this worker was loaded by verifying a one-time password rather than by the admin
   * building them in the wizard (CH-4506). Such a worker was already onboarded — possibly under
   * the old system, without ever setting a password — so their worker-facing pages stay
   * reachable even while `invitationStatus` is not ACCEPTED.
   */
  isOtpVerifiedLogin?: boolean;
  createdAt?: string;
  lastUpdatedAt?: string;
  companies?: {
    hasUniversalBadge: boolean;
    createdAt: string;
    companyWorkerId: string;
    discontinueWorkerCompanyResponse: any;
    id: string;
    name: string;
  }[];
  pictureUrl?: string;
  trades?: Array<{
    id: string;
    name: string;
  }>;
  isSupervisor?: boolean | null;
  isAffiliatedToLaborUnion?: boolean | null;
  isSkilled?: boolean | null;
  laborUnionNumber?: string;
  skilledTrades?: Array<{
    id: string;
    name: string;
    masterList: boolean;
  }>;
  governmentDocumentFiles?: IFile[];
  allCertifications?: any[];
  certificationGroups?: any[];
  workerPIIPinInformation?: {
    isPIIVisible: boolean;
    piiFieldVisibilityExpiredAt: string | null;
  };
  uploadingDetails?: {
    isOffline: boolean; // Indicates if the current worker has any data in the offline mode that we will need to upload in the worker status screen
    status: OnboardingStatus; // Current onboarding status of the worker
    pendingUploads: {
      workerCreation: boolean; // Indicates if the worker creation is pending upload
      workerPasswordCreation: boolean; // Indicates if the worker password creation is pending upload
      biometricConsent: boolean; // Indicates if the biometric consent acceptance is pending upload
      signature: boolean; // Indicates if a signature (registration or consent form) is pending upload
      consentAcceptances: boolean; // Indicates if consent agreement acceptances are pending upload
      customConsentAcceptances: boolean; // Indicates if project-level custom consent form acceptances are pending upload
      profilePicture: boolean; // Indicates if there is a profile picture pending upload
      govDocuments: boolean; // Indicates if there are government documents pending upload or deletion
      personalInformation: boolean; // Indicates if there is personal information pending upload
      contactInformation: boolean; // Indicates if there is contact information pending upload
      trades: boolean; // Indicates if there are trades pending upload
      employers: boolean; // Indicates if any employers need to be associated with the worker
      certifications: boolean; // Indicates if there are certifications pending upload
      consentFormData: boolean; // Indicates if consent form data is pending upload
    };
    sectionsCompleted?: {
      [key in StepKey]: boolean;
    };
    errors?: {
      pageUrl: string; // The page where the error occurred
      errorObject: any; // The error object or message
    };
  };
  db?: {
    id: string;
    projectId: string;
    createdByCompanyId?: string;
  };
  temp?:
    | {
        visitedWizardSteps?: StepKey[];
        /**
         * Id of the worker picked in the duplicate-worker modal, set when they are sent to the
         * login screen so the project/employers chosen beforehand can be attached once they
         * authenticate (CH-4652). Holds the id rather than a flag because the store is persisted:
         * it only counts once the same worker is actually loaded. Cleared by `useLogin` as soon as
         * the resume settles, on success and on failure alike.
         */
        resumeProjectOnboarding?: string;
        [key: string]: any;
      }
    | any;
  assignedCompanies?: {
    id: string;
    name: string;
    workerProjectStatus: number;
    workerProjectId?: string | null;
  }[];
  isDrugTestingAvailable?: boolean;
}

export const initialOnboardingTempState = {
  visitedWizardSteps: [] as StepKey[],
};

const initialState: IOnboardingState = {
  id: null,
  userId: null,
  email: null,
  mobilePhoneNumber: null,
  phoneNumber: null,
  loginWith: null,
  inviteMethod: 0,
  isOtpVerifiedLogin: false,
  address: null,
  emergencyContactName: null,
  emergencyContactPhone: null,
  emergencyContactRelationship: null,
  canEditEmail: false,
  canEditMobilePhoneNumber: false,
  existingConsentFormForAllCompanies: null,
  status: null,
  createdAt: '',
  companies: [],
  temp: initialOnboardingTempState,
  pictureUrl: null,
  trades: [],
  isSupervisor: null,
  isAffiliatedToLaborUnion: null,
  isSkilled: null,
  laborUnionNumber: '',
  skilledTrades: [],
  governmentDocumentFiles: [],
  allCertifications: [],
  certificationGroups: [],
  acceptedStandardConsentIds: [],
  acceptedCustomConsentIds: [],
  uploadingDetails: {
    isOffline: false,
    status: OnboardingStatus.INCOMPLETE,
    pendingUploads: {
      workerCreation: false,
      workerPasswordCreation: false,
      biometricConsent: false,
      signature: false,
      consentAcceptances: false,
      customConsentAcceptances: false,
      profilePicture: false,
      govDocuments: false,
      personalInformation: false,
      contactInformation: false,
      trades: false,
      employers: false,
      certifications: false,
      consentFormData: false,
    },
    errors: {
      pageUrl: '',
      errorObject: null,
    },
  },
  assignedCompanies: [],
};

export default initialState;
export type { IOnboardingState };
